import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { isoDate } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";
import { ReportType } from "./report-types";
import { ReportListItem } from "./list-reports";

/**
 * Single-report fetch identity.
 *
 * `reportId` (a regen-stable ULID) is the canonical key — it is the only one
 * unique in general. `(type, periodStart)` only works when a type has exactly
 * one report per period: true for WasteSense (fixed weekly windows), false for
 * RoadSense (cumulative reports share a `periodStart`, differing only in
 * `periodEnd`). WasteSense shipped on `periodStart` and deliberately stays
 * there; it is the sole owner of the `periodStart`-keyed surface below.
 */
export const ItemByIdPath = z.object({
  type: ReportType,
  reportId: z.string(),
});
export type ItemByIdPath = z.output<typeof ItemByIdPath>;

/** WasteSense-legacy key; new types use {@link ItemByIdPath}. */
export const ItemPath = z.object({
  type: ReportType,
  periodStart: isoDate,
});
export type ItemPath = z.output<typeof ItemPath>;

/**
 * `payloadUrl` is a short-lived presigned S3 URL for the report's
 * `payload.json`, not the payload itself. A busy tenant's payload can exceed
 * Lambda's 6 MB synchronous response limit (design §8), so the client fetches
 * the JSON directly from S3 and narrows it with the plugin's schema there.
 *
 * `payloadS3Key` is surfaced (the listing endpoint strips it) so the
 * snapshot-url endpoint can validate a client-supplied snapshot key against
 * this report's prefix without the frontend reconstructing it from identity.
 */
export const ReportItem = ReportListItem.extend({
  payloadUrl: z.string(),
  payloadUrlExpiresInSeconds: z.number().int().positive(),
  payloadS3Key: z.string(),
});
export type ReportItem = z.output<typeof ReportItem>;

/**
 * Shared response for both item routes. Readiness maps to distinct status codes
 * so a polling client tells "doesn't exist" from "not ready" without a HEAD
 * probe (design §6.3): 404 = no matching row, 409 = row exists but
 * `generating`/`failed`.
 *
 * Hand-built `{ data }` envelope rather than `SingleResponse` from
 * `@visioni/a3s-client-api`, which hard-requires a camera-scoped `subject`
 * field that doesn't apply to tenant-grained reports.
 */
export const GetReportResponse = z.object({
  data: ReportItem,
});
export type GetReportResponse = z.output<typeof GetReportResponse>;

/**
 * Canonical fetch. The server resolves `reportId` → row via the reports table's
 * `reportId` GSI; that index does not exist yet, so this is the wire surface a
 * RoadSense-capable backend will implement.
 */
export const reportsItemByIdResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/items/by-id/{reportId}",
  resourcePathSchema: ItemByIdPath,
  scope: "tenant",
});

export const getReportByIdEndpoint = defineEndpoint(reportsItemByIdResource, {
  responseSchema: GetReportResponse,
});

/** WasteSense-legacy fetch; new types use {@link getReportByIdEndpoint}. */
export const reportsItemResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/items/{periodStart}",
  resourcePathSchema: ItemPath,
  scope: "tenant",
});

export const getReportEndpoint = defineEndpoint(reportsItemResource, {
  responseSchema: GetReportResponse,
});
